/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellEditingStartedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueParserParams,
} from '@ag-grid-community/core';
import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { EventManager } from '@angular/platform-browser';
import dayjs from 'dayjs';
import { first, isEqual, last, mean, omit, pick, sortBy, sum } from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  firstValueFrom,
  merge,
  Observable,
  Subscription,
} from 'rxjs';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { BlendedCurveModalDataModel } from '@features/blended-curve-modal/blended-curve-modal-data.model';
import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { TimelineQuery } from '@models/milestone-category/milestone/timeline.query';
import { TimelineService } from '@models/milestone-category/milestone/timeline.service';
import { PatientCurveState } from '@models/patient-curve/patient-curve.store';
import { SiteCurveQuery } from '@models/site-curve/site-curve.query';
import { SiteCurveService } from '@models/site-curve/site-curve.service';
import { SiteCurveModel } from '@models/site-curve/site-curve.store';

import { ButtonComponent } from '@shared/components/button/button.component';
import {
  CanvasChartComponent,
  CanvasComponentOptions,
} from '@shared/components/canvas-chart/canvas-chart.component';
import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import { EditableListDropdownComponent } from '@shared/components/editable-list-dropdown/editable-list-dropdown.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MenuComponent } from '@shared/components/menu/menu.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import {
  RemoveDialogComponent,
  RemoveDialogInput,
} from '@shared/components/remove-dialog/remove-dialog.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ApiService } from '@shared/services/api.service';
import {
  CurveType,
  DistributionMode,
  EntityType,
  EventType,
  listDriverSiteDistributionsQuery,
  PermissionType,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { TableService } from '@shared/services/table.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import {
  batchPromises,
  decimalAdd,
  decimalDifference,
  decimalEquality,
  decimalRoundingToNumber,
  decimalRoundingToString,
} from '@shared/utils';
import { AgGridGetData } from '@shared/utils/ag-grid-data.utils';
import { ExportType, FormGroupTyped, Nullable, Utils } from '@shared/utils/utils';

import { SiteBlendedCurveModalComponent } from './components/site-blended-curve-modal/site-blended-curve-modal.component';
import { SiteDriverUploadComponent } from './components/site-driver-upload/site-driver-upload.component';
import { SiteCurvesConstants } from './constant/site-curves.constants';
import { DriverSiteQuery } from './models/driver-site/driver-site.query';
import { DriverSiteService } from './models/driver-site/driver-site.service';
import { DriverSiteSettingModel } from './models/driver-site/driver-site.store';
import { SiteDistributionQuery } from './models/site-distribution/site-distribution.query';
import { SiteDistributionService } from './models/site-distribution/site-distribution.service';
import { CurveWorkflowDirective } from '../../../directives/curve-workflow.directive';
import { DistributionTimelineService } from '../../../services/distribution-timeline.service';
import { SiteGroupsQuery } from '../../models/site-group/site-groups.query';
import { SiteGroupsService } from '../../models/site-group/site-groups.service';

export type TotalDriverSiteDistribution = {
  __typename: 'DriverSiteDistribution';
  distribution_mode: DistributionMode;
  distribution_month: string;
  id: string;
  net_sites_per_month?: null | number;
  net_sites_per_month_percentage?: null | number;
  sites_activated?: null | number;
  sites_activated_percentage?: null | number;
  sites_closed?: null | number;
  sites_closed_percentage?: null | number;
  total_sites_activated?: null | number;
};

type ForecastSiteCurvesFormSetting = Nullable<
  Pick<
    DriverSiteSettingModel,
    | 'cohort_name'
    | 'discontinued_date'
    | 'first_initiated_date'
    | 'id'
    | 'number_of_sites'
    | 'ramp_time_in_weeks'
    | 'target_patient_count'
  >
>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [CurveWorkflowDirective],
  imports: [
    MenuComponent,
    IconComponent,
    EditableListDropdownComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    CanvasChartComponent,
    ButtonComponent,
    TooltipDirective,
    ExportExcelButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
  ],
  selector: 'aux-forecast-site-curves',
  styleUrls: ['forecast-site-curves.component.scss'],
  templateUrl: './forecast-site-curves.component.html',
})
export class ForecastSiteCurvesComponent implements OnDestroy, OnInit {
  driverSiteQuery = inject(DriverSiteQuery);
  organizationQuery = inject(OrganizationQuery);
  workflowCtx = inject(CurveWorkflowDirective);

  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private distributionTimelineService = inject(DistributionTimelineService);
  private driverSiteService = inject(DriverSiteService);
  private eventManager = inject(EventManager);
  private eventService = inject(EventService);
  private fb = inject(FormBuilder);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private siteCurveQuery = inject(SiteCurveQuery);
  private siteCurveService = inject(SiteCurveService);
  private siteDistributionQuery = inject(SiteDistributionQuery);
  private siteDistributionService = inject(SiteDistributionService);
  private siteGroupQuery = inject(SiteGroupsQuery);
  private siteGroupService = inject(SiteGroupsService);
  private timelineQuery = inject(TimelineQuery);
  private timelineService = inject(TimelineService);
  private vendorsService = inject(OrganizationService);

  saveBtnTitle = signal<string>('');
  wasGridChanged = signal<boolean>(false);

  userHasModifyPermissions = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_MODIFY_SITE_CURVE],
  });

  disableModifySiteCurve = computed(() => {
    return !this.userHasModifyPermissions() || this.workflowCtx.isLocked();
  });
  disableModifySiteCurveTooltip = computed(() => {
    if (!this.userHasModifyPermissions()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return this.workflowCtx.tooltip();
  });

  avg$ = this.driverSiteQuery.selectAll().pipe(
    debounceTime(200),
    map((siteSettings) => {
      const total_number_of_sites = sum(siteSettings.map((ss) => ss.number_of_sites));
      const first_initiated_date = siteSettings
        .map((ss) => ss.first_initiated_date)
        .sort((a, b) => Date.parse(a || '') - Date.parse(b || ''))[0];
      const discontinued_dates = siteSettings
        .map((ss) => ss.discontinued_date)
        .sort((a, b) => Date.parse(a || '') - Date.parse(b || ''));
      const discontinued_date = discontinued_dates[discontinued_dates.length - 1];
      const ramp_time_in_weeks = mean(siteSettings.map((ss) => ss.ramp_time_in_weeks));
      const target_patient_count = mean(siteSettings.map((ss) => ss.target_patient_count));

      return {
        discontinued_date,
        first_initiated_date,
        ramp_time_in_weeks,
        target_patient_count,
        total_number_of_sites,
      };
    })
  );
  btnLoading$ = new BehaviorSubject(false);
  currentSiteGroupId = '';
  curveControl = new FormControl('');
  static readonly DISABLED_BUTTON_TITLE =
    'The site curve is unable to be saved until the following issue is resolved: Sites Closed should be equal to Sites Activated';
  driverSettingId$ = new BehaviorSubject<null | string>(null);
  editCell = false;
  editedRows = new Set<string>();
  editMode$ = new BehaviorSubject(false);
  fg = new FormGroup({
    settings: new FormArray<FormGroupTyped<Partial<ForecastSiteCurvesFormSetting>>>([]),
  });
  fgListener: Subscription | undefined;
  gridApi!: GridApi;
  gridData$ = new BehaviorSubject<TotalDriverSiteDistribution[]>([]);
  timelineList$ = new BehaviorSubject<{ endDate?: string; startDate?: string }>({
    endDate: '',
    startDate: '',
  });
  gridOptions = {
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellClass: SiteCurvesConstants.getCellClasses(this.editMode$),
      cellClassRules: {
        '!bg-aux-error': (params) => {
          let ret = false;

          switch (params.colDef.field) {
            case 'net_sites_per_month':
              if (
                !this.isBlended$.getValue() &&
                Utils.isNegativeAndNotCloseEnoughToZero(params.data.net_sites_per_month)
              ) {
                ret = true;
                this.isNetSiteNegative$.next(true);
              }

              break;
            case 'sites_activated':
            case 'sites_closed':
              if (params.data.distribution_month === 'TOTAL' && !this.isBlended$.getValue()) {
                ret = !decimalEquality(params.data.sites_closed, params.data.sites_activated);
                this.isSaveBtnDisabled = ret;
                this.saveBtnTitle.set(ret ? ForecastSiteCurvesComponent.DISABLED_BUTTON_TITLE : '');
              }

              break;
            default:
              break;
          }

          return ret;
        },
      },
      cellStyle: (params) => {
        switch (params.colDef.field) {
          case 'net_sites_per_month_percentage':
          case 'sites_activated_percentage':
          case 'sites_closed_percentage':
            return this.editCell
              ? { 'justify-items': 'end', opacity: 0.7 }
              : { 'justify-item': 'end' };
          default:
            return undefined;
        }
      },
      editable: () => {
        return this.editMode$.getValue();
      },
      headerClass: SiteCurvesConstants.getHeaderClasses(this.editMode$),
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        if (!params.editing) {
          switch (params.event.key) {
            case 'Backspace':
            case 'Delete':
              if (this.editMode$.getValue()) {
                TableService.clearCellRange(params, () => this.calculateSiteCurves());
                this.onCellValueChanged();
              }

              return true;
            default:
              return false;
          }
        }

        return false;
      },
    },
    ...SiteCurvesConstants.GRID_OPTIONS,
    columnDefs: SiteCurvesConstants.columnDefs(
      this.valueFormatter,
      this.numberParser,
      this.editCell,
      this.timelineList$
    ),
    excelStyles: SiteCurvesConstants.GRID_OPTIONS_EXEL_STYLES,
    processCellFromClipboard: (params) => {
      return Utils.castStringToNumber(params.value).toString();
    },
  } as GridOptions;
  initialValue: TotalDriverSiteDistribution[] = [];
  isBlended$ = new BehaviorSubject(false);
  isFirstTimeInitialization = true;
  isFromScratch = false;
  isNetSiteNegative$ = new BehaviorSubject(false);
  isSaveBtnDisabled = false;
  loading$ = new BehaviorSubject(false);
  monthList: { end_date: Date | string; start_date: string } = { end_date: '', start_date: '' };
  multiChart$: Observable<CanvasComponentOptions> = this.gridData$.pipe(
    map((data) => {
      const labels = data.map(({ distribution_month }) =>
        Utils.dateFormatter(distribution_month, { day: undefined, year: '2-digit' })
      );

      return SiteCurvesConstants.multiChartOptions(data, labels);
    })
  );
  removedRows: string[] = [];
  setSiteGroupSubscription!: Subscription;
  setTimelineSubscription!: Subscription;
  showEnrollmentSettings = this.launchDarklyService.$select(
    (flags) => flags.section_site_driver_assumptions
  );
  siteCurveGroupList: SiteCurveModel[] = [];
  siteCurveGroups: SiteCurveModel[] = [];
  siteCurveGroups$ = new BehaviorSubject<SiteCurveModel[]>([]);
  siteCurveListOptions: EditableListDropdownItem[] = [];
  userHasUploadSiteDriverPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPLOAD_SITE_DRIVER],
  });

  ngOnInit() {
    this.siteCurveGroups$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((items) => {
      this.updateSiteCurveList(items);
    });

    this.curveControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const driverPatientGroup =
        this.siteCurveGroupList?.find((item) => item.name === value) || null;

      this.selectCurve(driverPatientGroup);
    });

    this.vendorsService
      .get()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const vendors = this.organizationQuery.getAllVendors();

        if (vendors.length === 1) {
          this.organizationStore.setActive(vendors[0].id);
        } else {
          // reset any older selected vendors.
          this.organizationStore.setActive(null);
        }
      });

    this.mainQuery
      .select('trialKey')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          this.loading$.next(true);

          return combineLatest([this.siteCurveService.get(), this.siteGroupService.get()]);
        })
      )
      .subscribe(() => {
        this.curveControl.setValue('');
        this.isFirstTimeInitialization = true;
        this.monthList.start_date = '';
        this.setSiteGroups();
        this.loading$.next(false);
      });

    // todo duplicate code 16 line 4 different places
    this.eventManager.addEventListener(document.body, 'keydown', (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Tab':
          this.gridApi.stopEditing();
          setTimeout(() => {
            const getCellCor = this.gridApi.getFocusedCell();

            this.gridApi.startEditingCell({
              colKey: getCellCor?.column?.getColId() || '',
              rowIndex: getCellCor?.rowIndex || 0,
            });
          }, 0);
          break;
        default:
          break;
      }
    });

    this.driverSiteQuery
      .selectAll()
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(200))
      .subscribe((siteSettings) => {
        const fa = this.fg.controls.settings;

        fa.clear();
        siteSettings.map((ss) => this.createSiteSettingFB(ss)).forEach((x) => fa.push(x));
        this.listenForChanges();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    if (this.fgListener) {
      this.fgListener.unsubscribe();
    }
  }

  autoSize() {
    this.gridApi.sizeColumnsToFit();
  }

  private blendedCurveModalParams(
    item: null | SiteCurveModel
  ): BlendedCurveModalDataModel<SiteCurveModel> {
    return {
      availableGroups: this.siteCurveGroups
        .filter((group) => !group.is_blended && group.site_group_id)
        .filter(
          (value, index, self) =>
            index === self.findIndex((group) => group.driver_setting_id === value.driver_setting_id)
        ),
      blendedCurve: item,
      existingCurvesNames: this.siteCurveGroupList
        .map((group) => group.name)
        .filter((name) => name !== item?.name),
      text: {
        subTitle: 'Select Site Groups',
        title: 'Blended Site Curve',
      },
    };
  }

  calculatePinnedBottomData() {
    const columnsWithAggregation = [
      'sites_activated',
      'sites_closed',
      'net_sites_per_month',
      'net_sites_per_month_percentage',
      'total_sites_activated',
      'sites_activated_percentage',
      'sites_closed_percentage',
    ] as const;

    return this.gridData$.getValue().reduce(
      (acc, val) => {
        for (const key of columnsWithAggregation) {
          const currentVal = acc[key];
          const additionalVal = val[key];

          if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
            acc[key] = decimalAdd(currentVal, additionalVal);
          }
        }

        acc.net_sites_per_month = 0;
        acc.total_sites_activated = acc.sites_activated;

        return acc;
      },
      {
        net_sites_per_month: 0,
        net_sites_per_month_percentage: 0,
        sites_activated: 0,
        sites_activated_percentage: 0,
        sites_closed: 0,
        sites_closed_percentage: 0,
        total_sites_activated: 0,
      }
    );
  }

  calculateSiteCurves() {
    let totalNetSiteEnrolled = 0;
    let totalSiteActivated = 0;
    let totalSiteClosed = 0;
    let percentTotal = 0;
    let isNegativeSite = false;

    this.gridData$.getValue().forEach((x: TotalDriverSiteDistribution) => {
      x.sites_activated = decimalRoundingToNumber(x.sites_activated || 0, 8);
      x.sites_closed = decimalRoundingToNumber(x.sites_closed || 0, 8);

      const activeLessClosed = decimalDifference(x.sites_activated, x.sites_closed);

      totalNetSiteEnrolled = decimalAdd(totalNetSiteEnrolled, activeLessClosed);
      totalSiteActivated = decimalAdd(totalSiteActivated, x.sites_activated);
      totalSiteClosed = decimalAdd(totalSiteClosed, x.sites_closed);

      x.net_sites_per_month = decimalRoundingToNumber(totalNetSiteEnrolled || 0, 8);
      x.total_sites_activated = decimalRoundingToNumber(totalSiteActivated || 0, 8);

      if (Utils.isNegativeAndNotCloseEnoughToZero(x.net_sites_per_month)) {
        isNegativeSite = true;
      }

      percentTotal = decimalAdd(percentTotal, x.net_sites_per_month);
    });

    this.isNetSiteNegative$.next(isNegativeSite);

    this.gridData$.getValue().forEach((x: TotalDriverSiteDistribution) => {
      x.net_sites_per_month_percentage = Utils.calculateAsPercentage(
        percentTotal,
        x.net_sites_per_month || 0
      );

      x.sites_activated_percentage = Utils.calculateAsPercentage(
        totalSiteActivated,
        x.sites_activated || 0
      );

      x.sites_closed_percentage = Utils.calculateAsPercentage(totalSiteClosed, x.sites_closed || 0);
    });

    this.gridData$.next(this.gridData$.getValue());
    this.gridApi.refreshCells();
  }

  cancelEditMode() {
    let isNewData = false;

    this.gridData$.next(this.initialValue);
    this.gridData$.getValue().forEach((row) => {
      isNewData ||= this.isNewDataInRow(row);
    });

    if (isNewData && this.isFromScratch) {
      this.populateGridDataWithTimeline();
    } else {
      this.setSiteGroups();
    }

    if (this.editMode$.getValue()) {
      this.gridApi.stopEditing(true);
    }

    this.editMode$.next(false);
    this.isNetSiteNegative$.next(false);
    this.editCell = false;
    this.gridApi.refreshCells({ force: true });
  }

  async canDeactivate(): Promise<boolean> {
    const hasChanges = this.didGridHasChanges();

    if (hasChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  cellValueChanged() {
    this.calculateSiteCurves();
  }

  cloneSite() {
    const sData = sortBy(this.siteDistributionQuery.getAll(), 'distribution_month');

    if (!this.monthList.start_date) {
      if (this.setTimelineSubscription) {
        this.setTimelineSubscription.unsubscribe();
      }

      this.setTimelineSubscription = this.timelineService
        .getTimelineItems()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
      this.timelineQuery
        .select()
        .pipe(take(2))
        .subscribe(({ items }) => {
          const start_date = first(items)?.contract_start_date;
          let end_date = Utils.dateParse(`${last(items)?.contract_end_date.slice(0, 8)}01`);

          items.forEach((i) => {
            const parsedDate = Utils.dateParse(`${i.contract_end_date.slice(0, 8)}01`);

            if (parsedDate > end_date) {
              end_date = parsedDate;
            }
          });

          if (start_date) {
            this.monthList.start_date = start_date;
            this.monthList.end_date = end_date;
            this.setGridData(sData);
          }
        });
    } else {
      this.setGridData(sData);
    }
  }

  constructor() {
    this.timelineQuery
      .selectStartEndDateTrial()
      .pipe(takeUntilDestroyed())
      .subscribe((dates) => {
        this.timelineList$.next(dates);
      });

    this.editMode$.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.gridApi) {
        this.gridApi.refreshHeader();
      }
    });

    toObservable(this.showEnrollmentSettings)
      .pipe(
        switchMap((flag) => {
          if (flag) {
            return this.driverSiteService.get();
          }

          return EMPTY;
        }),
        takeUntilDestroyed()
      )
      .subscribe(() => {});
  }

  createSiteSettingFB({
    cohort_name,
    discontinued_date,
    first_initiated_date,
    id,
    number_of_sites,
    ramp_time_in_weeks,
    target_patient_count,
  }: Partial<DriverSiteSettingModel>) {
    return this.fb.group<Partial<ForecastSiteCurvesFormSetting>>({
      cohort_name,
      discontinued_date,
      first_initiated_date,
      id,
      number_of_sites,
      ramp_time_in_weeks,
      target_patient_count,
    });
  }

  async deleteCurve(siteCurveGroup: EditableListDropdownItem) {
    const driverSiteGroup = this.siteCurveGroupList.find(
      (item) => item.name === siteCurveGroup.name
    );

    if (!driverSiteGroup) {
      return;
    }

    if (driverSiteGroup.organizations_with_forecasts.length > 0) {
      const removeDialogInput: RemoveDialogInput = {
        cannotDeleteMessage: `${driverSiteGroup.name} cannot be deleted because it is being used in other areas of the application.<br>Please first remove the dependencies below and then ${driverSiteGroup.name} can be successfully deleted.<br>`,

        routeInputs: [
          {
            componentName: 'Forecast Methodology:',
            link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
            name: driverSiteGroup.name ? [driverSiteGroup.name] : [],
          },
        ],
      };

      const resp = this.overlayService.openPopup({
        content: RemoveDialogComponent,
        data: removeDialogInput,
        settings: {
          header: 'Remove Site Curve',
          primaryButton: {
            disabled: true,
            label: 'Remove',
          },
        },
      });

      await firstValueFrom(resp.afterClosed$);
    } else {
      this.overlayService.openPopup<
        { message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          message: `Are you sure you want to remove Site Curve ${driverSiteGroup.name}?`,
        },
        settings: {
          header: 'Remove Site Curve',
          primaryButton: {
            action: async (instance) => {
              instance?.ref.close();
              await this.siteCurveService.removeSiteCurve(driverSiteGroup);
              this.unselectCurveAndResetView();
            },
            label: 'Remove',
          },
        },
      });
    }
  }

  didGridHasChanges(): boolean {
    const fieldsToCompare = ['sites_activated', 'sites_closed'];
    const currentValuesToCompare = AgGridGetData(<GridApi>this.gridApi).map((curve) =>
      pick(curve, fieldsToCompare)
    );
    const initialValuesToCompare = this.initialValue.map((curve) => pick(curve, fieldsToCompare));

    const castToNumberArray = (colData: Partial<TotalDriverSiteDistribution>[]) => {
      const castedData = colData;

      castedData.forEach((val) => {
        val.sites_activated = !val.sites_activated ? 0 : Number(val.sites_activated);
        val.sites_closed = !val.sites_closed ? 0 : Number(val.sites_closed);
      });

      return castedData;
    };

    const castedCurrent = castToNumberArray(currentValuesToCompare);
    const castedInitial = castToNumberArray(initialValuesToCompare);

    return !isEqual(castedCurrent, castedInitial) && this.editMode$.getValue();
  }

  editCurve(siteCurveGroup: EditableListDropdownItem | null = null) {
    const driverSiteGroup = siteCurveGroup
      ? this.siteCurveGroupList.find((item) => item.name === siteCurveGroup.name) || null
      : null;

    const modalRef = this.overlayService.openPopup<
      unknown,
      {
        data?: {
          name?: string;
        };
      }
    >({
      data: this.blendedCurveModalParams(driverSiteGroup),
      modal: SiteBlendedCurveModalComponent,
      settings: {
        header: `${driverSiteGroup ? 'Edit' : 'Create'} Blended Site Curve`,
        primaryButton: {
          label: `${driverSiteGroup ? 'Update' : 'Create'} Curve`,
        },
      },
    });

    modalRef.afterClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ data }) => {
      // cancel modal will be null
      if (data?.data?.name) {
        this.curveControl.setValue(data.data.name);
      }
    });
  }

  editGrid() {
    this.editCell = true;
    this.gridApi.refreshCells({ force: true });
    this.gridApi.startEditingCell({
      colKey: 'sites_activated',
      rowIndex: 0,
    });
  }

  getFilePath(id: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/sites/${id}/site-driver/`;
  }

  isNewDataInRow = (row: TotalDriverSiteDistribution) => {
    return (
      row.id === '' && (!!row.sites_activated || !!row.sites_closed || !!row.net_sites_per_month)
    );
  };

  listenForChanges() {
    if (this.fgListener) {
      this.fgListener.unsubscribe();
    }

    const fa = this.fg.controls.settings;

    if (fa) {
      this.fgListener = merge(...fa.controls.map((x) => x.valueChanges)).subscribe((value) => {
        if (value.id) {
          this.editedRows.add(value.id);
        }
      });
    }
  }

  //don't allow entry of non-numeric or negative numbers
  numberParser(params: ValueParserParams): number | string {
    const num =
      params.newValue === params.oldValue
        ? params.oldValue
        : Utils.castStringToNumber(params.newValue).toString();

    return (Number.isNaN(num) ? params.oldValue : Math.abs(num)).toString();
  }

  onAddCohort() {
    const fa = this.fg.controls.settings;

    fa.push(this.createSiteSettingFB({}));
  }

  onCellValueChanged(): void {
    this.wasGridChanged.set(this.didGridHasChanges());
    const title = this.saveBtnTitle();

    this.saveBtnTitle.set(
      !this.wasGridChanged() ? 'You need to make one or more changes to save' : title
    );
  }

  onDataRendered({ api }: { api: GridApi }) {
    api.sizeColumnsToFit();
  }

  onEditClick(): void {
    this.initialValue = [];
    this.gridData$.value.forEach((data) => {
      this.initialValue.push({ ...data });
    });
    this.editMode$.next(true);
    this.editGrid();
    this.onCellValueChanged();
  }

  async onExportForSiteDriver() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next(true);

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.SITE_CURVE,
          filename: `${trialName}_site_curves`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  onGridReady(e: GridReadyEvent) {
    this.gridApi = e.api;
    this.gridData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const pinnedBottomData = { ...this.calculatePinnedBottomData(), distribution_month: 'TOTAL' };

      this.gridApi.setGridOption('pinnedBottomRowData', [pinnedBottomData]);
    });
  }

  onRemoveCohort(i: number, set: FormGroup) {
    const { id } = set.value;

    if (id) {
      this.removedRows.push(id);
    }

    const fa = this.fg.controls.settings;

    fa.removeAt(i);
  }

  async onSaveAll() {
    this.btnLoading$.next(true);
    const isInvalid = this.showErrors();

    if (isInvalid) {
      this.btnLoading$.next(false);
      this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);

      return;
    }

    if (this.isFromScratch) {
      void this.processSitesCreatedFromScratch();
    } else if (this.fg.valid && !this.loading$.getValue()) {
      this.loading$.next(true);

      const settings = (this.fg.value.settings || []) as Partial<ForecastSiteCurvesFormSetting>[];

      const thingsToUpdate = settings.filter((x) => !x.id || this.editedRows.has(x.id));

      const allErrors: string[][] = [];

      const gData = this.gridData$.getValue();

      if (thingsToUpdate.length > 0) {
        const responses = await batchPromises(gData, (p) =>
          this.siteDistributionService.updateSiteDistribution(p)
        );

        if (!responses.every((x) => x)) {
          this.btnLoading$.next(false);
          this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);
          this.cancelEditMode();
          this.btnLoading$.next(false);

          return;
        }

        this.editedRows.clear();
        this.overlayService.success('Sites Table updated successfully!');
      }

      if (this.removedRows.length) {
        const errors = await this.driverSiteService.remove(this.removedRows);

        allErrors.push(...errors);
        this.removedRows = [];
        this.gridData$.next([]);
        this.gridApi.refreshCells({ force: true });
      }

      if (allErrors.length) {
        this.overlayService.error(...allErrors);
      } else {
        this.overlayService.success();
      }

      this.loading$.next(false);
      this.cancelEditMode();
    }

    this.btnLoading$.next(false);
  }

  onSiteDriverUploadClick() {
    type InstanceType = null | SiteDriverUploadComponent;
    this.overlayService.openPopup({
      content: SiteDriverUploadComponent,
      settings: {
        header: 'Upload Site Driver',
        primaryButton: {
          action: (instance: InstanceType) => instance?.onUpload(),
          label: 'Upload',
        },
      },
    });
  }

  populateGridDataWithTimeline() {
    this.timelineService.getTimelineItems().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.timelineQuery
      .select()
      .pipe(take(2))
      .subscribe(({ items }) => {
        const start_date = first(items)?.contract_start_date;
        let end_date = Utils.dateParse(`${last(items)?.contract_end_date.slice(0, 8)}01`);

        items.forEach((i) => {
          const parsedDate = Utils.dateParse(`${i.contract_end_date.slice(0, 8)}01`);

          if (parsedDate > end_date) {
            end_date = parsedDate;
          }
        });

        if (start_date) {
          this.monthList.start_date = start_date;
          this.monthList.end_date = end_date || '';
          this.setGridData([]);
        }
      });
  }

  async processSitesCreatedFromScratch() {
    this.loading$.next(true);
    const thingsToUpdate: TotalDriverSiteDistribution[] = [];

    this.gridApi.forEachNode((node) => {
      thingsToUpdate.push(node.data);
    });
    const data = thingsToUpdate.map((row) => ({
      month: Utils.dateFormatter(row.distribution_month, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      }),
      sites_activated: row.sites_activated,
      sites_closed: row.sites_closed,
    }));
    const key = `${this.getFilePath(
      this.curveControl.value !== '' ? this.currentSiteGroupId.toString() : 'Site-Curve'
    )}site-curve-from-scratch.csv`;
    const blob = new Blob([Utils.objectToCsv(data)], { type: 'text/csv' });
    const fileSuccess = await this.apiService.uploadFile(key, new File([blob], key));

    if (fileSuccess) {
      const { errors, success } = await firstValueFrom(
        this.eventService.processEvent$({
          bucket_key: `public/${key}`,
          entity_id: this.curveControl.value !== '' ? this.currentSiteGroupId.toString() : '',
          entity_type: EntityType.SITE,
          payload: JSON.stringify({
            should_show_on_document_library: false,
          }),
          type: EventType.SITE_DRIVER_TEMPLATE_UPLOADED,
        })
      );

      if (success) {
        // allows time for SITE_DRIVER_TEMPLATE_UPLOADED event to complete
        await new Promise((resolve) => setTimeout(resolve, 5000));
        await firstValueFrom(this.siteCurveService.get().pipe(take(1)));
        await firstValueFrom(this.siteGroupService.get().pipe(take(1)));
        const { data: sData } = await firstValueFrom(
          this.siteDistributionService
            .getSiteCurveDistributions(
              this.curveControl.value !== '' ? this.currentSiteGroupId.toString() : ''
            )
            .pipe(take(1))
        );

        this.isFromScratch = false;
        this.setSiteGroups();

        if (sData) {
          this.setGridData(sData);
        }

        if (this.editMode$.getValue()) {
          this.gridApi.stopEditing();
          this.editMode$.next(false);
        }

        this.editCell = false;
        this.gridApi.refreshCells({ force: true });
        this.overlayService.success();
      } else {
        this.overlayService.error(errors);
      }
    } else {
      this.overlayService.error('There was an error uploading the file. Please try again');
    }

    this.loading$.next(false);
  }

  rowPinnedCheck(event: CellEditingStartedEvent) {
    if (event.node.rowPinned) {
      this.gridApi.stopEditing();
    }
  }

  saveEditMode = async () => {
    this.btnLoading$.next(true);
    const isInvalid = this.showErrors();

    if (isInvalid) {
      this.btnLoading$.next(false);
      this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);

      return;
    }

    let isNewData = false;

    this.gridData$.getValue().forEach((row) => {
      isNewData ||= this.isNewDataInRow(row);
    });

    if (isInvalid) {
      return;
    }

    if (this.isFromScratch || isNewData) {
      await this.processSitesCreatedFromScratch();
    } else {
      const sData = this.gridData$.getValue();
      const responses = await batchPromises(
        sData.filter((s) => s.id),
        (p) => this.siteDistributionService.updateSiteDistribution(p)
      );

      if (!responses.every((x) => x)) {
        this.btnLoading$.next(false);
        this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);

        return;
      }

      this.overlayService.success('Sites Table updated successfully!');

      const { errors, success } = await firstValueFrom(
        this.eventService.processEvent$({
          entity_id:
            this.currentSiteGroupId === null || this.currentSiteGroupId === ''
              ? ''
              : this.currentSiteGroupId.toString(),
          entity_type: EntityType.DRIVER,
          type: EventType.SITE_DRIVER_DISTRIBUTION_UPDATED,
        })
      );

      if (success) {
        this.overlayService.success();
      } else {
        this.overlayService.error(errors);
      }

      this.setSiteGroups();
      this.cancelEditMode();
    }

    this.cloneSite();
    this.editMode$.next(false);
    this.btnLoading$.next(false);
  };

  selectCurve(item: null | SiteCurveModel): void {
    this.loading$.next(true);
    this.currentSiteGroupId = item?.site_group_id || '';

    if (item) {
      if (this.editMode$.getValue()) {
        this.gridApi.stopEditing();
      }

      this.editMode$.next(false);
      this.editCell = false;

      if (!item?.driver_setting_id || item.driver_setting_id === '') {
        this.populateGridDataWithTimeline();
        this.isBlended$.next(false);
        this.isFromScratch = true;
      } else {
        this.isFromScratch = false;
        this.driverSettingId$.next(item.driver_setting_id);
        this.isBlended$.next(!!item?.is_blended);

        this.siteDistributionService
          .getSiteCurveDistributions(item?.site_group_id)
          .pipe(take(1))
          .subscribe(() => {
            if (!this.isFromScratch) {
              this.cloneSite();
            } else {
              this.isFromScratch = true;
              this.populateGridDataWithTimeline();
            }
          });
      }
    }

    this.loading$.next(false);
  }

  // eslint-disable-next-line max-lines-per-function
  setGridData(sData: listDriverSiteDistributionsQuery[]) {
    const { end_date, start_date } = this.monthList;
    const clonedSite: TotalDriverSiteDistribution[] = [];

    const { endDate, startDate } = this.distributionTimelineService.getDistributionTimeline(
      {
        endDate: dayjs(end_date).format('YYYY-MM-DD'),
        startDate: start_date,
      },
      sData
    );

    const parsedEndDate = dayjs(endDate).toDate();

    if (start_date) {
      const month_list: { label: string; value: string }[] = [];
      let date = Utils.dateParse(`${startDate.slice(0, 8)}01`);

      let totalSiteActivated = 0;

      while (date <= parsedEndDate) {
        const value = new Intl.DateTimeFormat('fr-CA', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(date);

        date = Utils.addMonths(date, 1);

        if (sData.length !== 0) {
          const suitableElementIndex = sData.findIndex((x) => x.distribution_month === value);

          // eslint-disable-next-line max-depth
          if (suitableElementIndex === -1) {
            const l: TotalDriverSiteDistribution = {
              __typename: 'DriverSiteDistribution',
              distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
              distribution_month: value,
              id: uuidv4(),
              net_sites_per_month: 0,
              sites_activated: 0,
              sites_closed: 0,
              total_sites_activated: 0,
            };

            void this.siteDistributionService.createSiteDistribution({
              ...omit(l, ['__typename', 'total_sites_activated', 'id']),
              driver_setting_id: this.driverSettingId$.getValue() || '',
              driver_site_distribution_id: l.id,
            });
            clonedSite.push(l);
          } else {
            clonedSite.push(sData[suitableElementIndex]);
          }
        } else {
          month_list.push({
            label: Utils.dateFormatter(value, { day: undefined, year: '2-digit' }),
            value,
          });
          const l: TotalDriverSiteDistribution = {
            __typename: 'DriverSiteDistribution',
            distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
            distribution_month: value,
            id: uuidv4(),
            net_sites_per_month: 0,
            sites_activated: 0,
            sites_closed: 0,
            total_sites_activated: 0,
          };

          // eslint-disable-next-line max-depth
          if (clonedSite.length < month_list.length) {
            clonedSite.push(l);
          }
        }
      }

      const newData = Utils.clone(clonedSite);

      newData.forEach((x) => {
        x.net_sites_per_month = decimalRoundingToNumber(x.net_sites_per_month || 0, 8);
        totalSiteActivated = decimalAdd(totalSiteActivated, x.sites_activated || 0, 8);
        x.total_sites_activated = totalSiteActivated;
      });
      this.gridData$.next(newData);
    }
  }

  setSiteGroups() {
    if (this.setSiteGroupSubscription) {
      this.setSiteGroupSubscription.unsubscribe();
    }

    this.setSiteGroupSubscription = combineLatest([
      this.siteCurveQuery.selectAll(),
      this.siteGroupQuery.selectAll(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([curves, siteGroups]) => {
        this.siteCurveGroups = curves;
        const siteGroupIds: string[] = [];

        curves?.forEach((c) => {
          siteGroupIds.push(c?.site_group_id);
        });
        const uniqSiteGroups: SiteCurveModel[] = [];

        siteGroups.forEach((siteGroup) => {
          if (!siteGroupIds.includes(siteGroup.id)) {
            uniqSiteGroups.push({
              __typename: 'DriverSiteGroup',
              constituent_site_groups: [],
              curve_type: CurveType.NET,
              driver_setting_id: '',
              is_blended: false,
              name: siteGroup.name,
              organizations_with_forecasts: [],
              showLine: false,
              site_group_id: siteGroup.id,
              site_group_ids: [],
            });
          }
        });
        const siteCurveGroupList: SiteCurveModel[] = [...curves, ...uniqSiteGroups];

        this.siteCurveGroups$.next(siteCurveGroupList);

        if (this.isFirstTimeInitialization) {
          if (siteCurveGroupList?.length > 1 && this.curveControl.value !== '') {
            const selectedItem = siteCurveGroupList.find((y) => y.name === this.curveControl.value);

            if (selectedItem) {
              this.isFromScratch = false;
              this.curveControl.setValue(selectedItem.name);
            } else if (siteCurveGroupList?.[0]) {
              this.isFromScratch = false;
              this.curveControl.setValue(siteCurveGroupList[0].name);
            } else {
              this.isFromScratch = true;
              this.unselectCurveAndResetView();
            }
          } else if (siteCurveGroupList?.[0]) {
            this.isFromScratch = false;
            this.curveControl.setValue(siteCurveGroupList[0].name);
          } else {
            this.isFromScratch = true;
            this.unselectCurveAndResetView();
          }
        }

        this.isFirstTimeInitialization = false;
      });
  }

  showErrors() {
    const rowNodes: IRowNode[] = [];
    let isThereAnyInvalidRow = false;
    const { sites_closed: total_closed_sites, total_sites_activated } =
      this.calculatePinnedBottomData();

    this.gridApi.forEachNode((node) => {
      const { sites_activated, sites_closed } = node.data as TotalDriverSiteDistribution;

      if (Utils.isNumber(sites_activated) && Utils.isNumber(sites_closed)) {
        if (node.data.showError) {
          node.data.showError = false;
          rowNodes.push(node);
        }
      } else {
        isThereAnyInvalidRow = true;
        node.data.showError = true;
        rowNodes.push(node);
      }
    });

    if (!decimalEquality(total_sites_activated, total_closed_sites)) {
      isThereAnyInvalidRow = true;
    }

    this.gridApi.redrawRows({ rowNodes });

    return isThereAnyInvalidRow;
  }

  private unselectCurveAndResetView(): void {
    if (this.curveControl.value) {
      this.curveControl.setValue('');
      this.populateGridDataWithTimeline();
    } else {
      this.isFromScratch = true;
      this.populateGridDataWithTimeline();
    }
  }

  private updateSiteCurveList(items: SiteCurveModel[]): void {
    this.siteCurveGroupList = items || [];
    this.siteCurveListOptions =
      items?.map((item: PatientCurveState) => ({
        isDeletable: item.driver_setting_id.length > 1,
        isEditable: item.is_blended,
        name: item.name,
        showLine: item.showLine,
        value: item.name,
      })) || [];
  }

  valueFormatter(val: ValueFormatterParams) {
    const newValue = val.value === '0' ? 0 : val.value;

    return newValue ? decimalRoundingToString(newValue) : Utils.zeroHyphen;
  }
}
